'use client'

import { useState } from 'react'
import { ChevronRight, Coins, Ticket, TriangleAlert } from 'lucide-react'
import Input from '@/components/elements/Input'
import Modal from '@/components/elements/Modal'
import { toast } from '@/lib/toast'
import { useAuth } from '@/lib/commerce/hooks'
import { convertPointsApi } from '@/lib/commerce/services/loyalty-api'
import { classNames, formatNumber, formatPrice, getVendorPrimaryColor, resolveColorHex } from '@/utils/util'

export default function ConvertVoucherModal({ open, setOpen, vendor, program, balance, onConverted }: { open: boolean, setOpen: (open: boolean) => void, vendor: any, program: any, balance: number, onConverted: () => void }) {
  const { session } = useAuth()
  const primary = resolveColorHex(getVendorPrimaryColor(vendor))

  const [convertPoints, setConvertPoints] = useState('')
  const [converting, setConverting] = useState(false)

  const convertValue = (Number(convertPoints) || 0) * (program?.pointValue || 1)

  const handleConvert = async () => {
    const points = Number(convertPoints)
    if (!points || points < (program?.minPointsToConvert || 1)) return toast.error(`Хамгийн багадаа ${formatNumber(program?.minPointsToConvert || 1)} оноо хөрвүүлнэ`)
    if (points > (balance || 0)) return toast.error('Онооны үлдэгдэл хүрэлцэхгүй байна')
    setConverting(true)
    const { data, error } = await convertPointsApi({ vendorId: vendor?.id, session: session || undefined }, points)
    setConverting(false)
    if (error) return toast.error(error)
    if (!data) return toast.error('Алдаа гарлаа. Дахин оролдоно уу')
    toast.success('Ваучер амжилттай үүслээ')
    setOpen(false)
    setConvertPoints('')
    onConverted()
  }

  return (
    <Modal open={open} setOpen={setOpen} title="Оноог ваучер болгох" description="Цуглуулсан оноогоо ваучер болгон дараагийн захиалгадаа ашиглаарай" isLoading={converting} saveAction={handleConvert} saveText="Хөрвүүлэх" saveIcon={Ticket} showCancel={false} color={getVendorPrimaryColor(vendor)} isSaveActive={Number(convertPoints) >= (program?.minPointsToConvert || 1) && Number(convertPoints) <= (balance || 0)}>
      <div className="space-y-4">
        <div className="rounded-2xl p-4 bg-[#F1EEE7] flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="size-10 shrink-0 rounded-full inline-flex items-center justify-center text-white" style={{ background: primary }}>
              <Coins className="size-5" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/55">Онооны үлдэгдэл</div>
              <div className="text-lg font-bold font-gilroy tabular-nums leading-tight">{formatNumber(balance || 0)}</div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/55">Ханш</div>
            <div className="text-sm font-semibold leading-tight">1 оноо = {formatNumber(program?.pointValue || 1)}₮</div>
          </div>
        </div>

        <div>
          <Input label="Хөрвүүлэх оноо" type="number" value={convertPoints} onChange={(e: any) => setConvertPoints(e.target.value.replace(/[^0-9]/g, ''))} placeholder={String(program?.minPointsToConvert || 100)} color={getVendorPrimaryColor(vendor)} />
          <div className="flex gap-2 mt-2">
            {[25, 50, 100].map((percent) => {
              const value = Math.floor(((balance || 0) * percent) / 100)
              return (
                <button key={percent} type="button" disabled={value < 1} className={classNames('px-3.5 py-1.5 rounded-full border text-xs font-semibold transition-colors disabled:opacity-40 cursor-pointer', Number(convertPoints) === value && value > 0 ? 'bg-foreground text-cream border-foreground' : 'border-foreground/15 text-foreground/55 hover:text-foreground')} onClick={() => setConvertPoints(String(value))}>
                  {percent === 100 ? 'Бүгд' : `${percent}%`}
                </button>
              )
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-foreground/10 p-4 flex items-center justify-center gap-4">
          <div className="text-center">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/55">Оноо</div>
            <div className="font-semibold tabular-nums">{formatNumber(Number(convertPoints) || 0)}</div>
          </div>
          <div className="size-8 rounded-full inline-flex items-center justify-center bg-[#F1EEE7] text-foreground/70">
            <ChevronRight className="size-4" strokeWidth={2.5} />
          </div>
          <div className="text-center">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/55">Ваучерын дүн</div>
            <div className="text-xl font-bold font-gilroy tabular-nums" style={{ color: primary }}>{formatPrice(convertValue)}</div>
          </div>
        </div>

        <div className="rounded-2xl bg-orange-50 border border-orange-200 p-4">
          <div className="flex items-center gap-2 text-orange-800">
            <TriangleAlert className="size-4 shrink-0" strokeWidth={2} />
            <span className="text-xs font-semibold">Хөрвүүлэхээсээ өмнө анхаараарай</span>
          </div>
          <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-orange-800/90 list-disc pl-5">
            <li>Ваучер болгосон оноог буцааж оноо болгох боломжгүй.</li>
            {program?.voucherPartialUse ? (
              <li>Ваучерын ашиглаагүй үлдэгдэл хадгалагдаж, дараагийн захиалгад ашиглагдана.</li>
            ) : (
              <li>Ваучер нэг захиалгад бүтнээрээ зарцуулагдах ба захиалгын дүн ваучераас бага бол зөрүү буцаан олгогдохгүй.</li>
            )}
            {program?.voucherExpireDays ? <li>Үүссэн ваучер {program.voucherExpireDays} хоногийн дотор хүчинтэй.</li> : null}
          </ul>
        </div>
      </div>
    </Modal>
  )
}
